/*
 * dm-shipping.js
 * Pure free-shipping math and money helpers. All amounts are integer cents.
 * Works in the browser (window.DMShipping) and in node (require) for tests.
 * The real shipping charge is set by Shopify shipping rates in the admin;
 * this file only drives the progress bar and the estimated summary.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.DMShipping = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var FREE_SHIPPING_THRESHOLD = 12000; // $120.00
  var STANDARD_SHIPPING = 899; // $8.99

  function toInt(value) {
    var n = Math.round(Number(value));
    return isFinite(n) ? n : 0;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /* Merchandise subtotal after discounts, excluding shipping and tax.
     Accepts a Shopify /cart.js object or a plain number of cents. */
  function eligibleSubtotal(cartOrCents) {
    if (cartOrCents && typeof cartOrCents === 'object') {
      var cents = cartOrCents.total_price;
      if (cents === undefined || cents === null) cents = cartOrCents.items_subtotal_price;
      return Math.max(0, toInt(cents));
    }
    return Math.max(0, toInt(cartOrCents));
  }

  function remaining(subtotal, threshold) {
    threshold = threshold === undefined ? FREE_SHIPPING_THRESHOLD : threshold;
    return Math.max(0, threshold - eligibleSubtotal(subtotal));
  }

  /* 0 to 100, two decimals (8999 of 12000 is 74.99). */
  function progressPercent(subtotal, threshold) {
    threshold = threshold === undefined ? FREE_SHIPPING_THRESHOLD : threshold;
    if (threshold <= 0) return 100;
    var pct = (eligibleSubtotal(subtotal) / threshold) * 100;
    return Math.round(clamp(pct, 0, 100) * 100) / 100;
  }

  function isUnlocked(subtotal, threshold) {
    threshold = threshold === undefined ? FREE_SHIPPING_THRESHOLD : threshold;
    return eligibleSubtotal(subtotal) >= threshold;
  }

  /* Estimated standard shipping in cents. Empty cart has nothing to ship. */
  function shippingCost(subtotal, opts) {
    opts = opts || {};
    var threshold = opts.threshold === undefined ? FREE_SHIPPING_THRESHOLD : opts.threshold;
    var standard = opts.standard === undefined ? STANDARD_SHIPPING : opts.standard;
    var s = eligibleSubtotal(subtotal);
    var count = opts.itemCount;
    var empty = count !== undefined ? toInt(count) <= 0 : s <= 0;
    if (empty) return 0;
    return s >= threshold ? 0 : standard;
  }

  /* Everything the UI needs in one object. */
  function summarize(cartOrCents, opts) {
    opts = opts || {};
    var threshold = opts.threshold === undefined ? FREE_SHIPPING_THRESHOLD : opts.threshold;
    var standard = opts.standard === undefined ? STANDARD_SHIPPING : opts.standard;
    var itemCount = opts.itemCount;
    if (itemCount === undefined && cartOrCents && typeof cartOrCents === 'object') {
      itemCount = cartOrCents.item_count;
    }
    var subtotal = eligibleSubtotal(cartOrCents);
    var empty = itemCount !== undefined ? toInt(itemCount) <= 0 : subtotal <= 0;
    var shipping = shippingCost(subtotal, { threshold: threshold, standard: standard, itemCount: itemCount });
    var state = empty ? 'empty' : subtotal >= threshold ? 'unlocked' : 'below';
    return {
      state: state,
      empty: empty,
      subtotal: subtotal,
      threshold: threshold,
      remaining: empty ? threshold : remaining(subtotal, threshold),
      percent: empty ? 0 : progressPercent(subtotal, threshold),
      unlocked: state === 'unlocked',
      shipping: shipping,
      totalBeforeTax: empty ? 0 : subtotal + shipping
    };
  }

  /* True only when adding addCents moves a below-threshold cart to or past the threshold. */
  function wouldUnlock(subtotal, addCents, threshold) {
    threshold = threshold === undefined ? FREE_SHIPPING_THRESHOLD : threshold;
    var s = eligibleSubtotal(subtotal);
    return s < threshold && s + Math.max(0, toInt(addCents)) >= threshold;
  }

  /* Money formatting from integer cents. Uses Intl when present. */
  function formatMoney(cents, currency, locale) {
    var value = toInt(cents) / 100;
    currency = currency || 'USD';
    try {
      return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: currency }).format(value);
    } catch (e) {
      return (currency === 'USD' ? '$' : currency + ' ') + value.toFixed(2);
    }
  }

  return {
    FREE_SHIPPING_THRESHOLD: FREE_SHIPPING_THRESHOLD,
    STANDARD_SHIPPING: STANDARD_SHIPPING,
    eligibleSubtotal: eligibleSubtotal,
    remaining: remaining,
    progressPercent: progressPercent,
    isUnlocked: isUnlocked,
    shippingCost: shippingCost,
    summarize: summarize,
    wouldUnlock: wouldUnlock,
    formatMoney: formatMoney
  };
});
